/**
 * Etapa 3 do autocadastro — o aceite do edital do período e o aviso de
 * privacidade (RN-CP-25).
 *
 * O edital é o PDF que o DTP publicou no ciclo (F03), baixado pela rota
 * pública `GET /bff/v1/ciclo-inscricao-publico/edital/:cicloId`. A versão
 * aceita é a do ciclo, e é ela que fica registrada no envio — o servidor
 * recusa aceite de versão que não é a vigente.
 *
 * O aviso de tratamento de dados é o `LgpdNotice` do design system, na variante
 * com caixa de consentimento — o mesmo componente e o mesmo texto do resto do
 * sistema. A versão do aviso vem do rascunho (`lgpdVersao`).
 */

import { Download, FileSignature } from 'lucide-react';
import { useId } from 'react';

import { lerConfiguracao } from '@/nucleo/auth/configuracao';
import { LgpdNotice } from '@/ui/LgpdNotice';
import { Badge } from '@/ui/base/badge';
import { Button } from '@/ui/base/button';
import { Card, CardContent } from '@/ui/base/card';
import { Checkbox } from '@/ui/base/checkbox';
import { Label } from '@/ui/base/label';

import { enderecoDoEdital, type CicloDaInscricao } from './apiDaInscricao';

export interface EtapaAceiteDoEditalProps {
  ciclo: CicloDaInscricao;
  lgpdVersao: string;
  aceiteDoEdital: boolean;
  aoAceitarOEdital: (aceite: boolean) => void;
  consentimentoLgpd: boolean;
  aoConsentir: (consentimento: boolean) => void;
}

export function EtapaAceiteDoEdital({
  ciclo,
  lgpdVersao,
  aceiteDoEdital,
  aoAceitarOEdital,
  consentimentoLgpd,
  aoConsentir,
}: EtapaAceiteDoEditalProps) {
  const idDoAceite = useId();

  return (
    <div className="space-y-4">
      <Card className="border-dashed border-primary/30 bg-primary/5">
        <CardContent className="flex flex-col gap-3 p-4 sm:flex-row sm:items-start">
          <FileSignature aria-hidden="true" className="h-6 w-6 shrink-0 text-primary" />
          <div className="min-w-0 flex-1 space-y-1">
            <p className="text-sm font-semibold text-foreground">Edital do período {ciclo.nome}</p>
            <Badge variant="secondary">Versão {ciclo.editalVersao}</Badge>
          </div>
          {ciclo.editalDisponivel && (
            <Button asChild variant="outline" size="sm" className="shrink-0">
              <a
                href={enderecoDoEdital(ciclo.id, lerConfiguracao().api)}
                target="_blank"
                rel="noopener noreferrer"
              >
                <Download aria-hidden="true" />
                Baixar o edital (PDF)
              </a>
            </Button>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-4">
          <div className="flex items-start gap-3">
            <Checkbox
              id={idDoAceite}
              checked={aceiteDoEdital}
              onCheckedChange={(marcado) => aoAceitarOEdital(marcado === true)}
              className="mt-0.5"
            />
            <Label htmlFor={idDoAceite} className="text-sm font-normal leading-relaxed">
              Declaro que li e <strong>aceito integralmente</strong> os termos do edital (versão{' '}
              <strong>{ciclo.editalVersao}</strong>), e me comprometo a cumprir as exigências, os
              prazos e as obrigações nele estabelecidos.
            </Label>
          </div>
        </CardContent>
      </Card>

      <LgpdNotice
        purpose="para processar e acompanhar a sua inscrição no credenciamento do Transporte Escolar Gratuito"
        variant="consent"
        checked={consentimentoLgpd}
        onCheckedChange={aoConsentir}
      />
      <p className="text-xs text-muted-foreground">Versão do aviso de privacidade: {lgpdVersao}.</p>
    </div>
  );
}

export default EtapaAceiteDoEdital;
